import {type ReactNode, useId, useState} from "react";
import {Box, Button, Flex, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr} from "@chakra-ui/react";
import iso6391 from "iso-639-1";
import type {ICreditUsageRow} from "../../models/interfaces/credits.interface.ts";
import {focusRing} from "./creditsWidgetStyles.ts";
import {formatCredits, formatUtcDate, headerCellStyle, tableSx, textButtonStyle} from "./historyTableStyles.ts";

const PAGE_SIZE = 10;
// The table never wraps, so a long name or a long language list would push Credits out of the dialog: the name is
// clipped at 20rem and names past the third collapse into "+N more"; the full text stays on a tooltip.
const EPISODE_MAX_WIDTH = "80";
const VISIBLE_LANGUAGES = 3;

// U+2212, not the ASCII hyphen: it is as wide as a tabular digit, so the returned figure lines up with the charge.
const MINUS_SIGN = "\u2212";
const UNKNOWN_MINUTES = "\u2014";

const secondLineStyle = {
  fontSize: "xs",
  color: "n.500",
} as const;

const footnoteStyle = {
  fontSize: "xs",
  color: "n.500",
  mt: 2,
} as const;

const moreLanguagesStyle = {
  whiteSpace: "nowrap",
  color: "n.500",
  textDecoration: "underline dotted",
  cursor: "help",
  rounded: "sm",
  _focusVisible: focusRing,
} as const;

const languagesCell = (codes: string[]): ReactNode => {
  if (codes.length === 0) return "Transcription only";
  const names = codes.map((code) => iso6391.getName(code) || code);
  if (names.length <= VISIBLE_LANGUAGES) return names.join(", ");
  const everyName = names.join(", ");
  return (
    <>
      {`${names.slice(0, VISIBLE_LANGUAGES).join(", ")} `}
      <Tooltip label={everyName} placement="top" hasArrow>
        <Text as="span" tabIndex={0} aria-label={everyName} {...moreLanguagesStyle}>
          {`+${names.length - VISIBLE_LANGUAGES} more`}
        </Text>
      </Tooltip>
    </>
  );
};

interface UsageHistoryProps {
  rows: ICreditUsageRow[] | null;
  isUnavailable: boolean;
  hasMore: boolean;
  showMigrationNote: boolean;
  onRetry?: () => void;
}

const UsageHistory = ({rows, isUnavailable, hasMore, showMigrationNote, onRetry}: UsageHistoryProps) => {
  const headingId = useId();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  if (rows === null && !isUnavailable) return null;

  // Unlike PurchaseHistory there is no client-side sort: the server lists the newest episode first.
  const usage = isUnavailable ? [] : rows ?? [];
  const canShowMore = usage.length > visibleCount;
  // hasMore cannot fetch older episodes (no server paging yet); once every local row is visible it becomes a footnote.
  const showsLatestOnly = hasMore && usage.length > 0 && !canShowMore;

  return (
    <Box as="section" aria-labelledby={headingId} mt={6} data-testid="usage-history">
      <Text as="h3" id={headingId} className="text-balance" fontSize="md" fontWeight="semibold" color="n.700">
        Usage
      </Text>
      <Box mt={3} bg="white" rounded="xl" borderWidth="1px" borderColor="n.300" overflowX="auto">
        {isUnavailable ? (
          <Flex align="center" gap={1} minH={12} px={4}>
            <Text fontSize="sm" color="n.500">Usage is unavailable right now.</Text>
            {onRetry && <Button {...textButtonStyle} onClick={onRetry}>Retry</Button>}
          </Flex>
        ) : usage.length === 0 ? (
          <Flex align="center" minH={12} px={4}>
            <Text fontSize="sm" color="n.500">No usage yet.</Text>
          </Flex>
        ) : (
          <Table size="sm" aria-labelledby={headingId} whiteSpace="nowrap" sx={tableSx}>
            <Thead>
              <Tr>
                <Th {...headerCellStyle}>Date</Th>
                <Th {...headerCellStyle}>Episode</Th>
                <Th {...headerCellStyle} isNumeric>Minutes</Th>
                <Th {...headerCellStyle}>Languages</Th>
                <Th {...headerCellStyle} isNumeric>Credits</Th>
              </Tr>
            </Thead>
            <Tbody>
              {usage.slice(0, visibleCount).map((row) => (
                <Tr key={row.id} h={12} data-testid={`usage-row-${row.id}`}>
                  <Td color="n.500">
                    <time dateTime={row.createdAt}>{formatUtcDate(row.createdAt)}</time>
                  </Td>
                  <Td color="n.700" fontWeight="medium">
                    {row.projectName === null ? (
                      <Text as="span" color="n.500" fontStyle="italic">Deleted project</Text>
                    ) : (
                      <Tooltip label={row.projectName} placement="top" hasArrow>
                        <Text maxW={EPISODE_MAX_WIDTH} isTruncated title={row.projectName}>{row.projectName}</Text>
                      </Tooltip>
                    )}
                    {row.kind === "add_languages" && <Text {...secondLineStyle}>Add languages</Text>}
                  </Td>
                  <Td isNumeric color="n.700">{row.billedMinutes ?? UNKNOWN_MINUTES}</Td>
                  <Td color="n.700">{languagesCell(row.languages)}</Td>
                  <Td isNumeric color="n.700" fontWeight="medium">
                    {formatCredits(row.creditsCharged)}
                    {row.creditsReturned > 0 && (
                      <Text {...secondLineStyle}>{`${MINUS_SIGN}${formatCredits(row.creditsReturned)} returned`}</Text>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      {canShowMore && (
        <Flex justify="center" mt={2}>
          <Button {...textButtonStyle} onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
            Show more
          </Button>
        </Flex>
      )}
      {showsLatestOnly && <Text {...footnoteStyle}>{`Showing the latest ${usage.length} episodes.`}</Text>}
      {showMigrationNote && <Text {...footnoteStyle}>Episodes processed before the switch to credits are not listed.</Text>}
    </Box>
  );
};

export default UsageHistory;
